import { useState } from 'react';
import styled, { css } from 'styled-components';

import { focusVisible } from '../helpers/focusVisible';
import { ColorPaletteIcon } from '../icons';
import { palettes, tokens } from '../theme/tokens';
import { FlexGap } from './FlexGap';
import { Popover } from './Popover';
import { DisabledSecondary, PRESSED_INSET, Secondary } from './button/ButtonTypes';

const COLOR_FAMILIES = [
	palettes.vividPink,
	palettes.vibrantPeach,
	palettes.electricBlue,
	palettes.freshGreen,
	palettes.coralRed,
] as const;

const TONES = ['200', '400', '600'] as const;

export const DEFAULT_PALETTE_COLORS: readonly (readonly string[])[] = TONES.map((tone) =>
	COLOR_FAMILIES.map((family) => family[tone]),
);

const Trigger = styled.button<{ $open?: boolean; $disabled?: boolean }>`
	width: 28px;
	height: 28px;
	padding: 0;
	border-radius: ${tokens.radius.full}px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	user-select: none;
	flex-shrink: 0;
	transition: 0.1s ease-out all;

	${focusVisible}

	svg {
		display: block;
		width: 16px;
		height: 16px;
		color: inherit;
	}

	${(props) => (props.$disabled ? DisabledSecondary : Secondary)}
	color: ${(props) => (props.$disabled ? tokens.text.disabled : tokens.icon.secondary)};

	&:hover {
		color: ${(props) => (props.$disabled ? tokens.text.disabled : tokens.icon.primary)};
	}

	${(props) =>
		props.$open &&
		!props.$disabled &&
		css`
			background: ${tokens.surface.secondary};
			color: ${tokens.icon.primary};
			box-shadow: ${PRESSED_INSET}, ${tokens.shadow.button};
		`}

	${(props) =>
		props.$disabled &&
		css`
			pointer-events: none;
		`}
`;

const Grid = styled(FlexGap)`
	padding: ${tokens.padding.control}px;
	flex-direction: column;
`;

const ColorOption = styled.button<{ $color: string; $selected?: boolean }>`
	width: 24px;
	height: 24px;
	padding: 0;
	border-radius: ${tokens.radius.xs}px;
	background: ${(props) => props.$color};
	border: 1px solid rgba(26, 9, 44, 0.08);
	box-sizing: border-box;
	cursor: pointer;
	flex-shrink: 0;
	transition:
		0.1s ease-out transform,
		0.1s ease-out box-shadow;

	${focusVisible}

	&:hover {
		transform: scale(1.08);
	}

	${(props) =>
		props.$selected &&
		css`
			box-shadow:
				0 0 0 2px ${tokens.surface.white},
				0 0 0 4px ${tokens.border.focus};
		`}
`;

export type ColorPalettePickerProps = {
	value?: string;
	onChange: (color: string) => void;
	colors?: readonly (readonly string[])[];
	label?: string;
	disabled?: boolean;
};

export function ColorPalettePicker(props: ColorPalettePickerProps) {
	const [open, setOpen] = useState(false);
	const colorRows = props.colors ?? DEFAULT_PALETTE_COLORS;
	const normalizedValue = props.value?.toLowerCase();

	const close = () => setOpen(false);

	const contents = (
		<Grid gap={tokens.gap.element} role="group" aria-label={props.label ?? 'Color palette'}>
			{colorRows.map((row, rowIndex) => (
				<FlexGap key={rowIndex} gap={tokens.gap.element}>
					{row.map((color) => {
						const selected = normalizedValue === color.toLowerCase();

						return (
							<ColorOption
								key={color}
								type="button"
								$color={color}
								$selected={selected}
								aria-label={color}
								aria-pressed={selected}
								onClick={(event) => {
									event.stopPropagation();
									props.onChange(color);
									close();
								}}
							/>
						);
					})}
				</FlexGap>
			))}
		</Grid>
	);

	return (
		<Popover.Target
			visible={open}
			onDismiss={close}
			contents={contents}
			elevation="md"
			offset={4}
			style={{ display: 'inline-flex' }}
		>
			<Trigger
				type="button"
				$open={open}
				$disabled={props.disabled}
				disabled={props.disabled}
				aria-label={props.label ?? 'Pick color'}
				aria-haspopup="dialog"
				aria-expanded={open}
				onClick={(event) => {
					event.preventDefault();
					event.stopPropagation();
					if (!props.disabled) {
						setOpen((visible) => !visible);
					}
				}}
			>
				<ColorPaletteIcon size={16} />
			</Trigger>
		</Popover.Target>
	);
}
